"use client"

import React, { createContext, ReactNode, useContext } from 'react';
import { Logger, LoggerOptions, LogLevel } from './logger';

// Create a default logger
const defaultLogger = new Logger('App');

// Create the context
const LoggerContext = createContext<Logger>(defaultLogger);

interface LoggerProviderProps {
  children: ReactNode;
  options?: Partial<LoggerOptions>;
  source?: string;
}

/**
 * Provider component for the Logger
 */
export const LoggerProvider: React.FC<LoggerProviderProps> = ({
  children,
  options,
  source = 'App',
}) => {
  // Create a logger instance with the provided options
  const logger = React.useMemo(() => {
    return new Logger(source, options);
  }, [source, options]);

  return (
    <LoggerContext.Provider value={logger}>
      {children}
    </LoggerContext.Provider>
  );
};

/**
 * Hook to use the logger in components
 */
export const useLogger = (componentName?: string): Logger => {
  const contextLogger = useContext(LoggerContext);

  // If a component name is provided, return a child logger for that component
  if (componentName) {
    return contextLogger.createChildLogger(componentName);
  }

  return contextLogger;
};

/**
 * Direct export of the Logger and related types
 */
export type { Logger, LoggerOptions, LogLevel };

/**
 * Direct export of a default logger instance for simpler imports
 */
export { defaultLogger };

